/**
 * VirtualTable — TanStack Table + Virtual for large datasets with expandable sub-rows.
 *
 * Features:
 * - Global search (wildcard across all columns)
 * - Column filtering (text input per column header)
 * - Column reordering (drag-and-drop)
 * - Row expansion with different column layout for sub-rows
 * - Two-level headers: parent columns + sub-column headers
 * - Virtualization via @tanstack/react-virtual
 * - Compact, Excel-like layout
 */

import type { FilterFn } from '@tanstack/react-table'

// Register custom filter functions with TanStack Table's type system
declare module '@tanstack/table-core' {
  interface FilterFns {
    includesString: FilterFn<unknown>
    dateFilter: FilterFn<unknown>
    numericFilter: FilterFn<unknown>
  }
}

import { useRef, useState, useCallback, useMemo, useEffect, ReactNode } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
  ColumnDef,
  ColumnFiltersState,
  ColumnSizingState,
  ExpandedState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronRight, ChevronDown, ChevronsUpDown, Search, Copy, FilterX } from 'lucide-react'

interface SubColumnDef<TChild> {
  key: string
  header: string
  width?: string
  align?: 'left' | 'right' | 'center'
  render?: (value: unknown, row: TChild) => ReactNode
  /** Show full value as tooltip on hover and truncate display */
  truncate?: boolean
}

interface VirtualTableProps<TParent, TChild = unknown> {
  data: TParent[]
  columns: ColumnDef<TParent, unknown>[]
  /** Extract sub-rows from a parent row (for TanStack expansion) */
  getSubRows?: (row: TParent) => TParent[] | undefined
  /** Column definitions for sub-rows (rendered differently from parent) */
  subColumns?: SubColumnDef<TChild>[]
  /** Cast a TParent (sub-row) to TChild for rendering */
  asChild?: (row: TParent) => TChild
  estimateSize?: number
  overscan?: number
  filterPlaceholder?: string
  globalFilterPlaceholder?: string
  expandAllLabel?: string
  collapseAllLabel?: string
  rowCountLabel?: string
  subRowCountLabel?: string
  copyLabel?: string
  clearFiltersLabel?: string
  /** Render extra stats in the toolbar based on filtered data */
  renderExtraStats?: (filteredParents: TParent[], filteredChildren: TParent[]) => ReactNode
  /** Render extra toolbar content (e.g. export button) based on filtered data.
   *  Third arg carries active filter state (global search + per-column filters). */
  renderToolbarExtra?: (
    filteredParents: TParent[],
    filteredChildren: TParent[],
    filterInfo?: { globalFilter: string; columnFilters: ColumnFiltersState },
  ) => ReactNode
  /** Custom context menu items for parent rows */
  contextMenuItems?: (row: TParent) => { label: string; icon?: ReactNode; onClick: () => void }[]
  /** Custom context menu items for sub-rows */
  subContextMenuItems?: (row: TChild) => { label: string; icon?: ReactNode; onClick: () => void }[]
  /** Additional CSS class for parent rows */
  getRowClassName?: (row: TParent) => string
  /** Max height for the table container (e.g. '60vh', '500px'). Enables scrolling within the limit. */
  maxHeight?: string
}

/** Parse Finnish date string (DD.MM or DD.MM.YYYY) into a Date object. */
function parseFiDate(s: string, defaultYear: number): Date | null {
  const parts = s.split('.')
  if (parts.length < 2) return null
  const day = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10)
  if (isNaN(day) || isNaN(month) || day < 1 || day > 31 || month < 1 || month > 12) return null
  const year = parts.length >= 3 && parts[2] ? parseInt(parts[2], 10) : defaultYear
  if (isNaN(year)) return null
  const fullYear = year < 100 ? 2000 + year : year
  return new Date(fullYear, month - 1, day)
}

/** Debounced filter input for column headers. */
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: {
  value: string
  onChange: (value: string) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const t = setTimeout(() => onChange(value), debounce)
    return () => clearTimeout(t)
  }, [value, debounce, onChange])

  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}

export default function VirtualTable<TParent, TChild = unknown>({
  data,
  columns,
  getSubRows,
  subColumns,
  asChild,
  estimateSize = 28,
  overscan = 30,
  filterPlaceholder = 'Suodata...',
  globalFilterPlaceholder = 'Hae kaikista...',
  expandAllLabel = 'Laajenna kaikki',
  collapseAllLabel = 'Sulje kaikki',
  rowCountLabel,
  subRowCountLabel,
  copyLabel = 'Kopioi',
  clearFiltersLabel = 'Tyhjennä suodattimet',
  renderExtraStats,
  renderToolbarExtra,
  contextMenuItems,
  subContextMenuItems,
  getRowClassName,
  maxHeight,
}: VirtualTableProps<TParent, TChild>) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [globalFilterInput, setGlobalFilterInput] = useState('')

  // Debounce global filter (300ms)
  useEffect(() => {
    const t = setTimeout(() => setGlobalFilter(globalFilterInput), 300)
    return () => clearTimeout(t)
  }, [globalFilterInput])
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  // DebouncedInput already debounces 300ms — no extra debounce needed
  const [subColumnFilters, setSubColumnFilters] = useState<Record<string, string>>({})
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; value: string; row: TParent | null; subRow: TChild | null } | null>(null)

  // Close context menu on any click
  useEffect(() => {
    if (!ctxMenu) return
    const close = () => setCtxMenu(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [ctxMenu])

  const handleContextMenu = useCallback((e: React.MouseEvent, rawValue: unknown, row: TParent | null = null, subRow: TChild | null = null) => {
    e.preventDefault()
    const text = rawValue != null ? String(rawValue) : ''
    setCtxMenu({ x: e.clientX, y: e.clientY, value: text || ' ', row, subRow })
  }, [])

  const handleCopy = useCallback(() => {
    if (ctxMenu) {
      navigator.clipboard.writeText(ctxMenu.value)
      setCtxMenu(null)
    }
  }, [ctxMenu])

  // Sub-column filtering at data level — fast because `data` is stable (React Query cache)
  const filteredData = useMemo(() => {
    const activeKeys = subColumns
      ? subColumns.filter(sc => subColumnFilters[sc.key]).map(sc => ({
          key: sc.key,
          filter: subColumnFilters[sc.key].toLowerCase().replace(',', '.'),
          raw: subColumnFilters[sc.key],
        }))
      : []
    if (!activeKeys.length || !getSubRows || !asChild) return data

    return data.filter(parent => {
      const subs = getSubRows(parent)
      if (!subs?.length) return true
      return subs.some(sub => {
        const child = asChild(sub) as Record<string, unknown>
        for (const { key, filter, raw } of activeKeys) {
          const val = child[key]
          if (raw === '!') {
            if (val != null && String(val).trim() !== '') return false
          } else {
            if (val == null) return false
            if (!String(val).toLowerCase().replace(',', '.').includes(filter)) return false
          }
        }
        return true
      })
    })
  }, [data, subColumnFilters, subColumns, getSubRows, asChild])

  const table = useReactTable({
    data: filteredData,
    columns,
    columnResizeMode: 'onChange',
    state: {
      columnFilters,
      globalFilter,
      expanded,
      columnSizing,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onColumnSizingChange: setColumnSizing,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      filterFn: 'includesString',
      minSize: 30,
      maxSize: 600,
    },
    filterFns: {
      // Override built-in includesString: "!" matches empty/null values
      // Column filters target parent rows only (sub-rows inherit parent visibility)
      includesString: (row, columnId, filterValue) => {
        if (row.depth > 0) return true
        const filter = String(filterValue)
        const val = row.getValue(columnId)
        if (filter === '!') {
          return val == null || String(val).trim() === ''
        }
        if (val == null) return false
        return String(val).toLowerCase().includes(filter.toLowerCase())
      },
      // Date filter: DD.MM, DD.MM.YYYY, DD.MM-DD.MM (range)
      dateFilter: (row, columnId, filterValue) => {
        if (row.depth > 0) return true
        const filter = String(filterValue).trim()
        if (!filter) return true
        const raw = row.getValue(columnId)
        if (raw == null) return filter === '!'
        const d = new Date(String(raw))
        if (isNaN(d.getTime())) return false
        const day = d.getDate()
        const month = d.getMonth() + 1
        const year = d.getFullYear()
        // Range: DD.MM-DD.MM or DD.MM.YYYY-DD.MM.YYYY
        if (filter.includes('-') && !filter.startsWith('-')) {
          const [fromStr, toStr] = filter.split('-').map(s => s.trim())
          const from = parseFiDate(fromStr, year)
          const to = parseFiDate(toStr, year)
          if (from && to) {
            const ts = new Date(year, month - 1, day).getTime()
            return ts >= from.getTime() && ts <= to.getTime()
          }
        }
        // Exact: DD.MM, D.M, DD.MM.YYYY
        const target = parseFiDate(filter, year)
        if (target) {
          return day === target.getDate() && month === target.getMonth() + 1
            && (filter.split('.').length < 3 || year === target.getFullYear())
        }
        // Fallback: match against both padded and unpadded formats
        const padded = `${String(day).padStart(2,'0')}.${String(month).padStart(2,'0')}`
        const unpadded = `${day}.${month}`
        return padded.includes(filter) || unpadded.includes(filter)
      },
      // Numeric filter: supports >, >=, <, <=, !=, range (X-Y)
      numericFilter: (row, columnId, filterValue) => {
        if (row.depth > 0) return true
        const filter = String(filterValue).trim()
        if (!filter) return true
        const raw = row.getValue(columnId)
        if (filter === '!') return raw == null || String(raw).trim() === ''
        const num = raw != null ? parseFloat(String(raw)) : NaN
        if (isNaN(num)) return false
        // Operator matching
        const match = filter.match(/^(>=|<=|!=|>|<|=)\s*(.+)$/)
        if (match) {
          const [, op, valStr] = match
          const target = parseFloat(valStr)
          if (isNaN(target)) return false
          switch (op) {
            case '>': return num > target
            case '>=': return num >= target
            case '<': return num < target
            case '<=': return num <= target
            case '!=': return num !== target
            case '=': return num === target
            default: return false
          }
        }
        // Range: X-Y (only if not starting with minus = negative number)
        const rangeMatch = filter.match(/^(-?\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)$/)
        if (rangeMatch) {
          const lo = parseFloat(rangeMatch[1].replace(',', '.'))
          const hi = parseFloat(rangeMatch[2].replace(',', '.'))
          if (!isNaN(lo) && !isNaN(hi)) return num >= lo && num <= hi
        }
        // No operator — exact match or string includes
        const target = parseFloat(filter.replace(',', '.'))
        if (!isNaN(target)) return num === target
        return String(num).includes(filter)
      },
    },
    globalFilterFn: (row, _columnId, filterValue) => {
      // Global search: match parent fields AND sub-row fields
      if (!filterValue) return true
      if (row.depth > 0) return true // sub-rows always pass (parent decides)
      const search = String(filterValue).toLowerCase()
      const original = row.original as Record<string, unknown>
      for (const val of Object.values(original)) {
        if (val != null && typeof val !== 'object' && String(val).toLowerCase().includes(search)) return true
      }
      const subRows = original.subRows as Record<string, unknown>[] | undefined
      if (subRows) {
        for (const sub of subRows) {
          for (const val of Object.values(sub)) {
            if (val != null && typeof val !== 'object' && String(val).toLowerCase().includes(search)) return true
          }
        }
      }
      return false
    },
    filterFromLeafRows: false,
  })

  const headerGroups = table.getHeaderGroups()

  // Rows come directly from TanStack (sub-filtering already applied at data level)
  const rows = table.getRowModel().rows

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan,
  })

  const allExpanded = table.getIsAllRowsExpanded()

  // Count from filtered model (sub-filtering already applied at data level)
  const filteredRootRows = table.getFilteredRowModel().rows
  const parentCount = filteredRootRows.length
  const childCount = useMemo(
    () => filteredRootRows.reduce((acc, r) => acc + (r.subRows?.length || 0), 0),
    [filteredRootRows]
  )

  // Filtered data for extra stats render prop
  const filteredParentData = useMemo(
    () => filteredRootRows.map((r) => r.original),
    [filteredRootRows]
  )
  const filteredChildData = useMemo(
    () => filteredRootRows.flatMap((r) => (r.subRows || []).map((sr) => sr.original)),
    [filteredRootRows]
  )

  // Total column count: max of parent columns + expand col, or sub-columns + expand col
  const visibleColumns = table.getVisibleLeafColumns()
  const expandCol = getSubRows ? 1 : 0
  const subColCount = subColumns ? subColumns.length : 0
  const maxDataCols = Math.max(visibleColumns.length, subColCount)
  const totalCols = expandCol + maxDataCols

  // Calculate total table width from max of parent/sub column sizes
  const tableWidth = (expandCol ? 24 : 0) + Array.from({ length: maxDataCols }).reduce<number>((sum, _, i) => {
    const parentW = visibleColumns[i] ? visibleColumns[i].getSize() : 0
    const subW = subColumns?.[i]?.width ? parseInt(subColumns[i].width!, 10) || 0 : 0
    return sum + Math.max(parentW, subW)
  }, 0)

  return (
    <div
      className="flex flex-col h-full border rounded-lg overflow-hidden bg-white"
      style={maxHeight ? { maxHeight } : undefined}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b text-xs shrink-0">
        <div className="flex items-center gap-3">
          {/* Global search */}
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={globalFilterInput}
              onChange={(e) => setGlobalFilterInput(e.target.value)}
              placeholder={globalFilterPlaceholder}
              className="pl-7 pr-2 py-1 border rounded text-xs w-52 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          {(globalFilterInput || columnFilters.length > 0 || Object.keys(subColumnFilters).some(k => subColumnFilters[k])) && (
            <button
              onClick={() => {
                setGlobalFilterInput('')
                setGlobalFilter('')
                setColumnFilters([])
                setSubColumnFilters({})
              }}
              className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-gray-100 text-red-600"
            >
              <FilterX className="w-3 h-3" />
              {clearFiltersLabel}
            </button>
          )}
          {getSubRows && (
            <button
              onClick={() => table.toggleAllRowsExpanded(!allExpanded)}
              className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-gray-100 text-gray-700"
            >
              <ChevronsUpDown className="w-3 h-3" />
              {allExpanded ? collapseAllLabel : expandAllLabel}
            </button>
          )}
          {renderToolbarExtra && renderToolbarExtra(filteredParentData, filteredChildData, { globalFilter, columnFilters })}
        </div>
        <div className="text-gray-500 flex items-center gap-3">
          {rowCountLabel && <span>{parentCount} {rowCountLabel}</span>}
          {subRowCountLabel && childCount > 0 && (
            <span>{childCount} {subRowCountLabel}</span>
          )}
          {!rowCountLabel && !subRowCountLabel && (
            <span>{rows.length} rivi{rows.length !== 1 ? 'a' : ''}</span>
          )}
          {renderExtraStats && renderExtraStats(filteredParentData, filteredChildData)}
        </div>
      </div>

      {/* Scrollable container */}
      <div ref={parentRef} className="flex-1 overflow-auto">
        <table className="text-xs border-collapse" style={{ width: tableWidth, minWidth: '100%' }}>
          {/* Sticky header */}
          <thead className="sticky top-0 z-10">
            {/* Parent column headers */}
            {headerGroups.map((hg) => (
              <tr key={hg.id} className="bg-gray-100">
                {getSubRows && (
                  <th className="w-6 min-w-[24px] max-w-[24px] px-0 py-1 border-b border-r bg-gray-100" />
                )}
                {hg.headers.map((header, idx) => {
                  const isLast = idx === hg.headers.length - 1
                  const extraCols = maxDataCols - visibleColumns.length
                  const subW = subColumns?.[idx]?.width ? parseInt(subColumns[idx].width!, 10) : 0
                  const thW = Math.max(header.getSize(), subW)
                  return (
                    <th
                      key={header.id}
                      colSpan={isLast && extraCols > 0 ? 1 + extraCols : 1}
                      style={{ width: thW, maxWidth: thW, position: 'relative', overflow: 'visible' }}
                      className="px-2 py-1 text-left font-medium text-gray-600 uppercase tracking-wider border-b border-r bg-gray-100 select-none whitespace-nowrap"
                    >
                      <span className="block overflow-hidden text-ellipsis">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </span>
                      {/* Resize handle — wide transparent hit area */}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'absolute', right: -3, top: 0, width: 7, height: '100%', cursor: 'col-resize', zIndex: 1 }}
                        className="select-none touch-none group"
                      >
                        <div className={`absolute right-[3px] top-0 w-[1px] h-full ${
                          header.column.getIsResizing() ? 'bg-blue-500' : 'group-hover:bg-gray-400'
                        }`} />
                      </div>
                    </th>
                  )
                })}
              </tr>
            ))}
            {/* Parent filter row */}
            <tr className="bg-gray-50">
              {getSubRows && (
                <th className="w-6 min-w-[24px] max-w-[24px] px-0 py-0.5 border-b border-r bg-gray-50" />
              )}
              {headerGroups[0]?.headers.map((header, idx) => {
                const isLast = idx === headerGroups[0].headers.length - 1
                const extraCols = maxDataCols - visibleColumns.length
                return (
                  <th
                    key={header.id + '-filter'}
                    colSpan={isLast && extraCols > 0 ? 1 + extraCols : 1}
                    className="px-1 py-0.5 border-b border-r bg-gray-50"
                  >
                    {header.column.getCanFilter() ? (
                      <DebouncedInput
                        value={(header.column.getFilterValue() ?? '') as string}
                        onChange={(val) =>
                          header.column.setFilterValue(val || undefined)
                        }
                        placeholder={filterPlaceholder}
                        className="w-full px-1 py-0.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    ) : null}
                  </th>
                )
              })}
            </tr>
            {/* Sub-column headers (shown below parent headers) */}
            {subColumns && (
              <tr className="bg-blue-50/60">
                {getSubRows && (
                  <th className="w-6 min-w-[24px] max-w-[24px] px-0 py-1 border-b border-r bg-blue-50/60 text-gray-400">
                    <span className="text-[10px]">&#x2514;</span>
                  </th>
                )}
                {subColumns.map((sc) => (
                  <th
                    key={`sub-${sc.key}`}
                    style={sc.width ? { width: sc.width } : undefined}
                    className="px-2 py-1 text-left font-medium text-blue-600/80 text-[10px] uppercase border-b border-r bg-blue-50/60 whitespace-nowrap overflow-hidden text-ellipsis"
                  >
                    {sc.header}
                  </th>
                ))}
                {/* Fill remaining columns if parent has more */}
                {maxDataCols > subColCount && (
                  <th
                    colSpan={maxDataCols - subColCount}
                    className="border-b bg-blue-50/60"
                  />
                )}
              </tr>
            )}
            {/* Sub-column filter row */}
            {subColumns && (
              <tr className="bg-blue-50/30">
                {getSubRows && (
                  <th className="w-6 min-w-[24px] max-w-[24px] px-0 py-0.5 border-b border-r bg-blue-50/30" />
                )}
                {subColumns.map((sc) => (
                  <th key={`subf-${sc.key}`} className="px-1 py-0.5 border-b border-r bg-blue-50/30">
                    <DebouncedInput
                      value={subColumnFilters[sc.key] ?? ''}
                      onChange={(val) =>
                        setSubColumnFilters((prev) => ({ ...prev, [sc.key]: val }))
                      }
                      placeholder={filterPlaceholder}
                      className="w-full px-1 py-0.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </th>
                ))}
                {maxDataCols > subColCount && (
                  <th colSpan={maxDataCols - subColCount} className="border-b bg-blue-50/30" />
                )}
              </tr>
            )}
          </thead>

          {/* Virtual body */}
          <tbody>
            {/* Spacer for virtual offset */}
            {virtualizer.getVirtualItems().length > 0 && (
              <tr>
                <td
                  colSpan={totalCols}
                  style={{
                    height: virtualizer.getVirtualItems()[0]?.start ?? 0,
                    padding: 0,
                    border: 'none',
                  }}
                />
              </tr>
            )}

            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              const isChild = row.depth > 0

              if (isChild && subColumns && asChild) {
                // Render sub-row with custom columns
                const childData = asChild(row.original)
                return (
                  <tr
                    key={row.id}
                    ref={virtualizer.measureElement}
                    data-index={virtualRow.index}
                    className="bg-white hover:bg-gray-50"
                  >
                    {getSubRows && (
                      <td className="w-6 min-w-[24px] max-w-[24px] px-0 py-0.5 border-b border-r text-center text-gray-300">
                        <span className="text-[10px]">&#x2514;</span>
                      </td>
                    )}
                    {subColumns.map((sc, scIdx) => {
                      const val = (childData as Record<string, unknown>)[sc.key]
                      const rendered = sc.render
                        ? sc.render(val, childData)
                        : val != null
                          ? String(val)
                          : ''
                      const plainText = val != null ? String(val) : ''
                      const parentCol = visibleColumns[scIdx]
                      const subW = sc.width ? parseInt(sc.width, 10) : 0
                      const cellW = parentCol ? Math.max(parentCol.getSize(), subW) : subW
                      return (
                        <td
                          key={sc.key}
                          title={sc.truncate && plainText ? plainText : undefined}
                          style={cellW ? { width: cellW, maxWidth: cellW } : undefined}
                          className={`px-2 py-0.5 border-b border-r whitespace-nowrap overflow-hidden text-ellipsis text-gray-700 ${
                            sc.align === 'right' ? 'text-right' : ''
                          }`}
                          onContextMenu={(e) => handleContextMenu(e, val, null, childData)}
                        >
                          {rendered}
                        </td>
                      )
                    })}
                    {/* Fill remaining columns */}
                    {maxDataCols > subColCount && (
                      <td
                        colSpan={maxDataCols - subColCount}
                        className="px-2 py-0.5 border-b border-r"
                      />
                    )}
                  </tr>
                )
              }

              // Render parent row
              return (
                <tr
                  key={row.id}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                  className={`font-semibold bg-gray-100 hover:bg-gray-200 ${
                    getRowClassName?.(row.original) ?? ''
                  } ${row.getCanExpand() ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (row.getCanExpand()) row.toggleExpanded()
                  }}
                >
                  {getSubRows && (
                    <td className="w-6 min-w-[24px] max-w-[24px] px-0 py-0.5 border-b border-r text-center">
                      {row.getCanExpand() ? (
                        row.getIsExpanded() ? (
                          <ChevronDown className="w-3 h-3 text-gray-500 inline" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-gray-500 inline" />
                        )
                      ) : null}
                    </td>
                  )}
                  {row.getVisibleCells().map((cell, idx) => {
                    const isLast = idx === row.getVisibleCells().length - 1
                    const extraCols = maxDataCols - visibleColumns.length
                    const subW = subColumns?.[idx]?.width ? parseInt(subColumns[idx].width!, 10) : 0
                    const cellW = Math.max(cell.column.getSize(), subW)
                    return (
                      <td
                        key={cell.id}
                        colSpan={isLast && extraCols > 0 ? 1 + extraCols : 1}
                        style={{ width: cellW, maxWidth: cellW }}
                        className="px-2 py-0.5 border-b border-r whitespace-nowrap overflow-hidden text-ellipsis"
                        onContextMenu={(e) => handleContextMenu(e, cell.getValue(), row.original)}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}

            {/* Bottom spacer */}
            {virtualizer.getVirtualItems().length > 0 && (
              <tr>
                <td
                  colSpan={totalCols}
                  style={{
                    height:
                      virtualizer.getTotalSize() -
                      (() => {
                        const items = virtualizer.getVirtualItems()
                        return items.length > 0 ? items[items.length - 1].end : 0
                      })(),
                    padding: 0,
                    border: 'none',
                  }}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <div
          className="fixed z-50 bg-white border rounded shadow-lg py-1 text-xs"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
        >
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 w-full text-left text-gray-700"
          >
            <Copy className="w-3 h-3" />
            {copyLabel}
          </button>
          {ctxMenu.row && contextMenuItems?.(ctxMenu.row).map((item, i) => (
            <button
              key={i}
              onClick={item.onClick}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 w-full text-left text-gray-700"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
          {ctxMenu.subRow && subContextMenuItems?.(ctxMenu.subRow).map((item, i) => (
            <button
              key={`sub-${i}`}
              onClick={item.onClick}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 w-full text-left text-gray-700"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
